import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api";
import { UserDropdown } from "./UserDropdown";
import { useAuth } from "../hooks/useAuth";
import { agentKeys, statsKey } from "../queryKeys";

interface AccountStats {
  messages_this_month: number;
  conversations: number;
}

export function AccountLayout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const { data: agents = [] } = useQuery({
    queryKey: agentKeys.list(),
    queryFn: () => apiFetch<{ agents: unknown[] }>("/api/agents").then((r) => r.agents),
  });
  const { data: stats } = useQuery({
    queryKey: statsKey,
    queryFn: () => apiFetch<AccountStats>("/api/stats"),
  });

  const agentCount = agents.length;
  const statsFallback: AccountStats = { messages_this_month: 0, conversations: 0 };

  function handleSettingsClick() {
    window.location.href = "/settings";
  }

  const userName = user?.display_name || user?.email?.split("@")[0] || "User";
  const plan = user?.plan || "free";
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const isSettings = pathname === "/settings";

  return (
    <div className={`agent-editor-layout ${menuOpen ? "mobile-menu-open" : ""}`}>
      <div
        className="sidebar-backdrop"
        aria-hidden="true"
        onClick={() => setMenuOpen(false)}
      />

      <header className="mobile-top-bar">
        <button
          type="button"
          className="mobile-menu-btn"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
        >
          <span className="material-symbols-outlined">menu</span>
        </button>
        <span className="mobile-top-bar-title">Agent Builder</span>
        <span className="mobile-top-bar-spacer" />
      </header>

      <aside className="agent-editor-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-header-row">
            <div className="sidebar-logo-section">
              <div className="sidebar-logo-icon">
                <span className="material-symbols-outlined">smart_toy</span>
              </div>
              <h1 className="sidebar-logo-title">Agent Builder</h1>
            </div>
            <button
              type="button"
              className="sidebar-close-btn"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <p className="sidebar-logo-subtitle">AI Chat Management</p>
        </div>

        <nav className="agent-editor-nav">
          <a
            href="/dashboard"
            className="editor-nav-item active"
            onClick={() => setMenuOpen(false)}
          >
            <span className="material-symbols-outlined">dashboard</span>
            <span>Agents</span>
          </a>
        </nav>

        <div className="sidebar-footer-section">
          <UserDropdown
            userName={userName}
            userPlan={plan}
            onSettingsClick={() => { setMenuOpen(false); handleSettingsClick(); }}
          />
        </div>
      </aside>

      <main className="agent-editor-main">
        <header className="agent-editor-header">
          <div className="header-left">
            <h2 className="header-title">{isSettings ? "Settings" : "Agents"}</h2>
          </div>
          {!isSettings && (
            <div className="dashboard-stats">
              <span className="dash-stat">
                <strong>{agentCount ?? "—"}</strong> agents
              </span>
              <span className="dash-stat">
                <strong>{(stats ?? statsFallback).messages_this_month?.toLocaleString() ?? "—"}</strong> messages this month
              </span>
              <span className="dash-stat">
                <strong>{(stats ?? statsFallback).conversations?.toLocaleString() ?? "—"}</strong> conversations
              </span>
            </div>
          )}
        </header>

        <div className="agent-editor-content" style={{ overflow: "auto" }}>
          {children}
        </div>
      </main>
    </div>
  );
}
